/**
 * Generic 3D tilt card wrapper with glare and vinyl-peek.
 * Used for the featured album sleeve and the "other records" grid.
 * Mouse position drives perspective rotation and specular highlight.
 */
import { useState, useRef } from "react";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";

interface TiltCardProps {
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
  ariaLabel?: string;
  maxTilt?: number;
  vinylPeekOnHover?: boolean;
  isVinylOut?: boolean;
}

const TiltCard = ({ 
  children, 
  className, 
  onClick, 
  ariaLabel,
  maxTilt = 15,
  vinylPeekOnHover = false,
  isVinylOut = false,
}: TiltCardProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const [glarePosition, setGlarePosition] = useState({ x: 50, y: 50 });
  const containerRef = useRef<HTMLButtonElement>(null);

  const handleMouseMove = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!containerRef.current) return;
    
    const rect = containerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    
    // Calculate tilt
    const tiltX = ((y - centerY) / centerY) * -maxTilt;
    const tiltY = ((x - centerX) / centerX) * maxTilt;
    
    setTilt({ x: tiltX, y: tiltY });
    
    // Calculate glare position (percentage)
    const glareX = (x / rect.width) * 100;
    const glareY = (y / rect.height) * 100;
    setGlarePosition({ x: glareX, y: glareY });
  };

  const handleHoverEnd = () => {
    setIsHovered(false);
    setTilt({ x: 0, y: 0 });
    setGlarePosition({ x: 50, y: 50 });
  };

  return (
    <motion.button
      ref={containerRef}
      type="button"
      className={cn(
        "relative cursor-pointer",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-foreground focus-visible:ring-offset-2 focus-visible:ring-offset-background",
        className
      )}
      style={{ 
        perspective: "1000px",
        zIndex: isHovered ? 50 : 1,
      }}
      onClick={onClick}
      onMouseMove={handleMouseMove}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={handleHoverEnd}
      aria-label={ariaLabel}
    >
      {/* Vinyl peek effect - follows same tilt as sleeve */}
      {vinylPeekOnHover && !isVinylOut && (
        <motion.div
          className="absolute w-[90%] aspect-square rounded-full bg-[hsl(0,0%,8%)] pointer-events-none"
          style={{
            top: "50%",
            left: "50%",
            marginTop: "-45%",
            marginLeft: "-45%",
            zIndex: -1,
            transformStyle: "preserve-3d",
          }}
          animate={{
            x: isHovered ? "15%" : "0%",
            rotateX: isHovered ? tilt.x : 0,
            rotateY: isHovered ? tilt.y : 0,
            scale: isHovered ? 1.05 : 1,
          }}
          transition={{ 
            type: "spring",
            stiffness: 300,
            damping: 20,
          }}
        >
          {/* Vinyl edge highlight */}
          <div className="absolute inset-0 rounded-full border border-[rgba(255,255,255,0.15)]" />
          {/* Vinyl grooves hint */}
          <div className="absolute inset-[8%] rounded-full border border-[rgba(255,255,255,0.05)]" />
          <div className="absolute inset-[16%] rounded-full border border-[rgba(255,255,255,0.04)]" />
          <div className="absolute inset-[24%] rounded-full border border-[rgba(255,255,255,0.03)]" />
          <div className="absolute inset-[32%] rounded-full border border-[rgba(255,255,255,0.03)]" />
          {/* Center label area */}
          <div className="absolute inset-[35%] rounded-full bg-[hsl(0,0%,12%)]">
            <div className="absolute inset-[30%] rounded-full bg-[hsl(0,0%,8%)]" />
          </div>
        </motion.div>
      )}

      {/* 3D tilt container with thickness effect */}
      <motion.div
        className="relative w-full h-full"
        animate={{
          rotateX: isHovered ? tilt.x : 0,
          rotateY: isHovered ? tilt.y : 0,
          scale: isHovered ? 1.05 : 1,
        }}
        transition={{
          type: "spring",
          stiffness: 300,
          damping: 20,
        }}
        style={{ 
          transformStyle: "preserve-3d",
        }}
      >
        {/* Main sleeve face */}
        <div 
          className="relative w-full h-full rounded-[2px] overflow-hidden sleeve-worn"
          style={{ 
            transformStyle: "preserve-3d",
            boxShadow: isHovered 
              ? `
                0 ${20 + tilt.x * 1.5}px ${40 + Math.abs(tilt.x) * 2}px -8px rgba(0,0,0,0.5),
                0 ${35 + tilt.x * 2}px ${60 + Math.abs(tilt.x) * 3}px -12px rgba(0,0,0,0.35)
              `
              : "0 4px 12px -4px rgba(0,0,0,0.3)",
          }}
        >
          {children}

          {/* Glare effect overlay */}
          <motion.div
            className="absolute inset-0 pointer-events-none rounded-[2px]"
            animate={{ opacity: isHovered ? 0.35 : 0 }}
            transition={{ duration: 0.2 }}
            style={{
              background: `radial-gradient(circle at ${glarePosition.x}% ${glarePosition.y}%, rgba(255,255,255,0.6) 0%, rgba(255,255,255,0.15) 35%, transparent 65%)`,
            }}
          />

          {/* Inner edge highlights */}
          <div className="absolute inset-0 pointer-events-none rounded-[2px] border border-[rgba(255,255,255,0.08)]" />
        </div>

      </motion.div>
    </motion.button>
  );
};

export default TiltCard;
